import { cilCalendar, cilFolderOpen, cilWarning, cilZoom } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  CCard,
  CCardBody,
  CCardText,
  CCardTitle,
  CCol,
  CFormInput,
  CFormSelect,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
} from "@coreui/react";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import DataTable, { createTheme } from "react-data-table-component";
import { usePersistedState } from "components/hooks/usePersistedState";

const DataTableComponent = ({
  columns,
  data,
  years,
  selectedYear,
  onYearChange,
  onRowClicked,
  noData,
  loading,
  error,
  defaultSortFieldId,
  defaultSortAsc,
  serverSide = false,
  totalRows,
  perPage,
  page,
  searchValue,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  persistKey,
  pagination = true,
  search = true,
}) => {
  const tableRef = useRef(null);
  const location = useLocation();
  const storageKey = `dtc-state:${persistKey || location.pathname}`;
  // Remembers this table's own search/sort/pagination state across
  // unmount+remount (e.g. navigating to a detail page and back), so the
  // user finds the table exactly as they left it.
  const [tableState, setTableState] = usePersistedState(storageKey, {});

  const [internalSearchTerm, setInternalSearchTerm] = useState(
    () => tableState.search || "",
  );
  const searchTerm = serverSide ? searchValue ?? "" : internalSearchTerm;
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTableState((current) => ({ ...current, search: value }));
    if (serverSide) {
      onSearchChange && onSearchChange(value);
    } else {
      setInternalSearchTerm(value);
    }
  };
  const [filteredData, setFilteredData] = useState(data || []);

  // Create custom theme for dark mode
  createTheme("darkCustom", {
    text: {
      primary: "#FFFFFF",
      secondary: "#D3D3D3",
    },
    background: {
      default: "#212631",
    },
    divider: {
      default: "#3A4452",
    },
    highlightOnHover: {
      default: "#323A4A",
      text: "#FFFFFF",
    },
    striped: {
      default: "#262E3D",
      text: "#FFFFFF",
    },
  });

  const defaultNoDataCard = (
    <CCard
      className="border-0 mb-3"
      style={{
        background: "#f5f6fa",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CCardBody className="w-100 d-flex flex-column align-items-center justify-content-center text-center py-5">
        <CIcon icon={cilFolderOpen} size="xl" className="mb-3 text-muted" />
        <div className="h6 text-muted mb-2">Nenhum registo encontrado</div>
      </CCardBody>
    </CCard>
  );

  const isLoadingCard = (
    <CCard
      className="border-0"
      style={{
        background: "#f5f6fa",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CCardBody className="w-100 d-flex flex-column align-items-center justify-content-center text-center py-5">
        <CSpinner color="primary" style={{ width: "4rem", height: "4rem" }} />
        <CRow className="mt-3">
          <CCol className="text-center">
            <span className="text-muted fs-5 fw-semibold">
              A carregar os dados...
            </span>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  );

  const errorCard = (
    <CCard
      className="border-0"
      style={{
        background: "#f5f6fa",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CCardBody className="w-100 d-flex flex-column align-items-center justify-content-center text-center py-5">
        <div className="mb-3 d-flex flex-column align-items-center">
          <CIcon icon={cilWarning} size="3xl" className="text-danger mb-2" />
          <CCardTitle
            className="mb-2"
            style={{ fontSize: "2rem", fontWeight: 700 }}
          >
            Oops! Algo correu mal
          </CCardTitle>
        </div>
        <CCardText>
          Não foi possível obter os dados necessários para mostrar esta página.
        </CCardText>
      </CCardBody>
    </CCard>
  );

  const [theme, setTheme] = useState(
    document.documentElement.getAttribute("data-coreui-theme") === "dark"
      ? "darkCustom"
      : "light"
  );

  const customStyles = useMemo(
    () => ({
      headCells: {
        style: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "visible",
          textOverflow: "unset",
        },
      },
      cells: {
        style: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          overflow: "visible",
          textOverflow: "unset",
          width: "100%",
          maxWidth: "100%",
          lineHeight: "1.5",
        },
      },
      rows: {
        style: {
          cursor: onRowClicked ? "pointer" : "default",
          "&:hover": {
            backgroundColor: theme === "darkCustom" ? "#323A4A" : "#f0f0f0",
          },
        },
      },
    }),
    [theme, onRowClicked]
  );

  // Observe theme changes and update pagination button colors accordingly
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newTheme =
        document.documentElement.getAttribute("data-coreui-theme") === "dark"
          ? "darkCustom"
          : "light";
      if (newTheme !== theme) {
        setTheme(newTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-coreui-theme"],
    });

    return () => observer.disconnect();
  }, [theme]);

  // Filter data based on search term (skipped in server mode — `data` já vem
  // filtrado/paginado do backend, incluindo a pesquisa)
  useEffect(() => {
    if (serverSide) {
      setFilteredData(data || []);
      return;
    }
    if (error || loading) return;

    if (!searchTerm) {
      setFilteredData(data);
    } else {
      const lowercasedTerm = searchTerm.toLowerCase();
      setFilteredData(
        data.filter((row) =>
          Object.values(row).some(
            (value) =>
              value && value.toString().toLowerCase().includes(lowercasedTerm)
          )
        )
      );
    }
  }, [searchTerm, data, serverSide, error, loading]);

  const paginationOptions = {
    rowsPerPageText: "Linhas por página:",
    rangeSeparatorText: "de",
  };

  // Memoize the onYearChange function to prevent unnecessary re-renders
  const handleYearChange = useCallback(
    (e) => {
      const value = e.target.value;
      setTableState((current) => ({ ...current, selectedYear: value }));
      onYearChange(value);
    },
    [onYearChange, setTableState]
  );

  // Year is owned by the parent view's own state, so restoring it after a
  // remount means nudging the parent once via its own callback (this may
  // cause one extra fetch with the stale default year before the parent
  // re-fetches with the restored one — an acceptable trade-off for not
  // having to touch every view that uses a year filter).
  const restoredYearRef = useRef(false);
  useEffect(() => {
    if (restoredYearRef.current) return;
    restoredYearRef.current = true;
    if (
      years &&
      onYearChange &&
      tableState.selectedYear != null &&
      String(tableState.selectedYear) !== String(selectedYear)
    ) {
      onYearChange(tableState.selectedYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = useCallback(
    (column, sortDirection) => {
      setTableState((current) => ({
        ...current,
        sortFieldId: column.id,
        sortDirection,
      }));
    },
    [setTableState]
  );

  const handlePageChange = useCallback(
    (newPage, newTotalRows) => {
      setTableState((current) => ({ ...current, page: newPage }));
      onPageChange && onPageChange(newPage, newTotalRows);
    },
    [setTableState, onPageChange]
  );

  const handleRowsPerPageChange = useCallback(
    (newPerPage, newPage) => {
      setTableState((current) => ({
        ...current,
        rowsPerPage: newPerPage,
        page: newPage,
      }));
      onRowsPerPageChange && onRowsPerPageChange(newPerPage, newPage);
    },
    [setTableState, onRowsPerPageChange]
  );

  const effectiveDefaultSortFieldId = tableState.sortFieldId ?? defaultSortFieldId;
  const effectiveDefaultSortAsc =
    tableState.sortDirection != null
      ? tableState.sortDirection === "asc"
      : defaultSortAsc;
  const effectivePaginationDefaultPage = page ?? tableState.page ?? 1;
  const effectivePaginationPerPage = perPage ?? tableState.rowsPerPage ?? 10;

  const showYearSelect = Boolean(years && onYearChange);
  const showFilterBar = showYearSelect || search;

  return (
    <div className="table-container">
      {showFilterBar && (
        <CInputGroup className="mb-3">
          {showYearSelect && (
            <>
              <CInputGroupText>
                <CIcon icon={cilCalendar} size="xl" />
              </CInputGroupText>
              <CFormSelect
                style={{
                  width: "auto",
                  minWidth: "80px",
                  maxWidth: "150px",
                  flex: "0 0 auto",
                }}
                value={selectedYear}
                onChange={handleYearChange}
              >
                <option>Ano</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </CFormSelect>
            </>
          )}
          {search && (
            <>
              <CInputGroupText>
                <CIcon icon={cilZoom} size="xl" />
              </CInputGroupText>
              <CFormInput
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
                aria-label="Search"
              />
            </>
          )}
        </CInputGroup>
      )}

      {/* DataTable Component */}
      {error ? (
        errorCard
      ) : loading ? (
        isLoadingCard
      ) : (
        <DataTable
          ref={tableRef}
          data={filteredData || []}
          columns={columns}
          defaultSortFieldId={effectiveDefaultSortFieldId}
          defaultSortAsc={effectiveDefaultSortAsc}
          onSort={handleSort}
          className="table-striped"
          pagination={pagination}
          paginationServer={serverSide}
          paginationTotalRows={serverSide ? totalRows : undefined}
          paginationDefaultPage={effectivePaginationDefaultPage}
          paginationPerPage={effectivePaginationPerPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          paginationComponentOptions={paginationOptions}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          theme={theme}
          customStyles={customStyles}
          onRowClicked={onRowClicked}
          pointerOnHover={Boolean(onRowClicked)}
          highlightOnHover={Boolean(onRowClicked)}
          noDataComponent={noData || defaultNoDataCard}
        />
      )}

      <style>
        {`
               .rdt_Pagination select {
                  background-color: ${
                    theme === "darkCustom" ? "#212631" : "#fff"
                  } !important;
                  color: ${
                    theme === "darkCustom" ? "#fff" : "#0000008a"
                  } !important;
                  border: 1px solid #ccc;
               }

               /* Pagination Buttons Styling */
                  .rdt_Pagination button {
                  color: ${theme === "darkCustom" ? "#fff" : "#000"} !important;
               }

               /* SVG inside Pagination Buttons */
                  .rdt_Pagination button svg {
                  fill: ${
                    theme === "darkCustom" ? "#D3D3D3" : "#0000008a"
                  } !important;
               }

               /* Disabled Pagination Buttons */
               .rdt_Pagination button:disabled {
                  color: ${
                    theme === "darkCustom"
                      ? "rgba(211, 211, 211, 0.54)"
                      : "rgba(0, 0, 0, 0.18)"
                  } !important;
                  background-color: ${
                    theme === "darkCustom" ? "#212631" : "#fff"
                  } !important;
               }

               /* Disabled SVG inside Pagination Buttons */
               .rdt_Pagination button:disabled svg {
                  fill: ${
                    theme === "darkCustom"
                      ? "rgba(211, 211, 211, 0.54)"
                      : "rgba(0, 0, 0, 0.18)"
                  } !important;
               }

               .rdt_TableHead .rdt_TableCol > div > div {
                  white-space: normal !important;
                  word-break: break-word !important;
                  overflow: visible !important;
                  text-overflow: clip !important;
               }

               .rdt_TableCell > div {
                  white-space: normal !important;
                  word-break: break-word !important;
               }
            `}
      </style>
    </div>
  );
};

export default DataTableComponent;
